/**
 * Mailing — email marketing (contacts, segments, templates, campaigns,
 * workflows, outbox, settings).
 *
 * Backed by /compuzz/rest/v2/mailing. Each screen is its own route so the
 * browser back button and deep links behave; the shared sub-navigation lives
 * in mailing/common/shell.
 *
 * NOTE — every nested require() below lists its module as a string literal,
 * repetitive as that looks. The release build runs r.js with
 * `findNestedDependencies: true`, which resolves these by *static analysis*:
 * passing the module name through a variable makes the view invisible to the
 * optimiser, it never lands in source.min.js, and almond (no loader, unlike
 * RequireJS in dev) fails at runtime with "missing mailingCampaignsView".
 * Factor the shared part, never the module names.
 */
define('mailingRouter',[
  'require',
  'exports',
  'module',
  'marionette',
  'app',
  'settings',
  'itemPagesNameMixin',
  'entities/mailing'
], function (require, exports, module, Marionette, App, Settings, ItemPagesNameMixin, MailingApi) {
  'use strict';

  var MailingRouter = Marionette.AppRouter.extend({
    appRoutes: {
      'mailing': 'showCampaigns',
      'mailing/subscribers': 'showSubscribers',
      'mailing/segments': 'showSegments',
      'mailing/templates': 'showTemplates',
      'mailing/templates/:secId': 'showTemplateEditor',
      'mailing/campaigns': 'showCampaigns',
      'mailing/campaigns/:secId': 'showCampaignDetail',
      'mailing/personalised': 'showPersonalised',
      'mailing/personalised/:secId': 'showPersonalisedReview',
      'mailing/workflows': 'showWorkflows',
      'mailing/workflows/:secId': 'showWorkflowDetail',
      'mailing/queue': 'showQueue',
      'mailing/settings': 'showSettings'
    }
  });

  /**
   * Shared tail of every route: wait for the content region, drop the view in,
   * light up the menu entry. Takes the already-loaded view constructor, so the
   * module name stays a literal at each call site.
   */
  /**
   * Once per page load, on the first mailing screen: keep the backend's copy of
   * this environment's webshop map current (proofsheet button redirects).
   * Best effort — a failure only means the previous copy stays.
   */
  var webshopUrlsPushed = false;
  function pushWebshopUrls() {
    var urls = Settings.get('webshopUrl');
    if (webshopUrlsPushed || !urls) { return; }
    webshopUrlsPushed = true;
    MailingApi.pushWebshopUrls(urls);
  }

  function display(ContentShowController, View, activeItem, options) {
    pushWebshopUrls();
    ContentShowController.showContent().done(function () {
      var view = new View(options || {});
      App.regions.getRegion('main').currentView.getRegion('content').show(view);
      App.execute('set:active:nav:item', activeItem);
    });
  }

  var API = {
    showSubscribers: function () {
      require(['mailingSubscribersView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_SUBSCRIBERS);
      });
    },

    showSegments: function () {
      require(['mailingSegmentsView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_SEGMENTS);
      });
    },

    showTemplates: function () {
      require(['mailingTemplatesView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_TEMPLATES);
      });
    },

    showTemplateEditor: function (secId) {
      require(['mailingTemplateEditorView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_TEMPLATES, { secId: secId });
      });
    },

    showCampaigns: function () {
      require(['mailingCampaignsView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_CAMPAIGNS);
      });
    },

    showCampaignDetail: function (secId) {
      require(['mailingCampaignDetailView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_CAMPAIGNS, { secId: secId });
      });
    },

    showPersonalised: function () {
      require(['mailingPersonalisedView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_PERSONALISED);
      });
    },

    showPersonalisedReview: function (secId) {
      require(['mailingPersonalisedReviewView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_PERSONALISED, { secId: secId });
      });
    },

    showWorkflows: function () {
      require(['mailingWorkflowsView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_WORKFLOWS);
      });
    },

    showWorkflowDetail: function (secId) {
      require(['mailingWorkflowDetailView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_WORKFLOWS, { secId: secId });
      });
    },

    showQueue: function () {
      require(['mailingQueueView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_QUEUE);
      });
    },

    showSettings: function () {
      require(['mailingSettingsView', 'contentShowController'], function (View, Content) {
        display(Content, View, ItemPagesNameMixin.MAILING_SETTINGS);
      });
    }
  };

  App.on('mailing:show', function () {
    App.navigate('mailing/campaigns');
    API.showCampaigns();
  });

  App.on('before:start', function () {
    new MailingRouter({ controller: API });
  });

  module.exports = MailingRouter;
});

